import { useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { jsonStore } from "@/manager/cache/localdb";
import { pushInfoDataTemplateList } from "@/services/push-info-data-template-list";
import { ItemDetailData, knowledgeGetFolderItems } from "@/services/knowledge-get-folder-items";
import { agentMaterialSettings, AgentMaterialSettingsResponseData, TemplateMaterialSettingsData } from "@/services/agent-material-settings";
import { useParams } from "react-router-dom";
import { AgentMaterialSettingsUpdateRequest } from "@/services/agent-material-settings-update";
import { agentMaterialSettingsUpdate } from "@/services/agent-material-settings-update";
import { agentUserGlobalTemplatesList } from "@/services/agent-user-global-template-list";
import { agentUserActorsFilter, AgentUserActorsFilterRequest, PublicActorTemplateData } from "@/services/agent-user-actors-filter";
import { agentUserAudioTemplatesFilter, AgentUserAudioTemplatesFilterRequest, PublicAudioTemplateData } from "@/services/agent-user-audio-templates-filter";
import { useTranslation } from "react-i18next";
import { agentDeleteTemplate } from "@/services/agent-delete-template";

/** 短视频模板类型 */
export enum VIDEO_TEMPLATE_TYPE {
  /** 数字人口播 */
  VIRTUAL_MAN = 'virtualman',
  /** 真人口播 */
  REAL_MAN = 'realMan',
  /** 新闻混剪体 */
  NEWS_MIX_CUTTING = 'newsMixCutting',
  /** 口播混剪体 */
  ORAL_MIX_CUTTING = 'oralMixCutting',
  /** 文本展示体 */
  WRITING_SHOW = 'writingShow',
  /** 图片封面 */
  IMAGE_TOPIC = 'imageTopic',
}

/** 短视频模板场景 */
export enum VIDEO_TEMPLATE_SCENE {
  /** 口播体 */
  BROADCAST = 'broadcast',
  /** 混剪体 */
  MIXCUT = 'mixcut',
  /** 文本展示体 */
  TEXT = 'text',
}

/** 场景对应的模板类型列表 */
export const SCENE_TO_TEMPLATES_MAP = {
  [VIDEO_TEMPLATE_SCENE.BROADCAST]: [
    VIDEO_TEMPLATE_TYPE.VIRTUAL_MAN,
    VIDEO_TEMPLATE_TYPE.REAL_MAN
  ],
  [VIDEO_TEMPLATE_SCENE.MIXCUT]: [
    VIDEO_TEMPLATE_TYPE.NEWS_MIX_CUTTING,
    VIDEO_TEMPLATE_TYPE.ORAL_MIX_CUTTING
  ],
  [VIDEO_TEMPLATE_SCENE.TEXT]: [
    VIDEO_TEMPLATE_TYPE.WRITING_SHOW
  ],
} as const;

// 根据模板类型获取场景
export const getSceneByType = (type: VIDEO_TEMPLATE_TYPE): string | undefined => {
  for (const [scene, templates] of Object.entries(SCENE_TO_TEMPLATES_MAP)) {
    if ((templates as readonly string[]).includes(type)) {
      return scene;
    }
  }
  return undefined;
};

// 模板类型转换器，获取后端对应的 Scene Key（用于取值）
export const getEffectiveSceneKey = (scene: VIDEO_TEMPLATE_TYPE, isNote: boolean) => {
  const baseScene = getSceneByType(scene); // 得到 'broadcast' | 'mixcut' | 'text'
  if (!baseScene) return undefined;

  // 如果是笔记模式，进行映射转换
  if (isNote) {
    const noteMap: Record<string, string> = {
      [VIDEO_TEMPLATE_SCENE.BROADCAST]: 'broadcastNote',
      [VIDEO_TEMPLATE_SCENE.MIXCUT]: 'mixcutNote',
      // 如果 TEXT 场景未来也有笔记，可以在此添加
    };
    return noteMap[baseScene] || baseScene;
  }

  return baseScene;
};

// 获取更新接口需要的 scene 参数字符串（用于设值）
export const getUpdateSceneParam = (scene: VIDEO_TEMPLATE_TYPE, isNote: boolean) => {
  const baseScene = getSceneByType(scene);
  if (!baseScene) return undefined;

  // 如果是笔记，拼接特定后缀
  return isNote ? `${baseScene}_note` : baseScene;
};

// 素材设置更新事件
class MaterialSettingsUpdateEvent {
  private readonly eventName = 'MIUTA_MATERIAL_SETTINGS_UPDATE';

  listen(listener: () => void): () => void {
    window.addEventListener(this.eventName, listener);
    return () => {
      window.removeEventListener(this.eventName, listener);
    };
  }

  dispatch(): void {
    const event = new Event(this.eventName);
    window.dispatchEvent(event);
  }
}

type TemplateItem = {
  id: number;
  uuid: string;
  name: string;
  coverUrl: string;
  coverVideo?: string;
  createAt?: string;
  templateResource?: number;
  scene?: VIDEO_TEMPLATE_TYPE;
  voiceModelId?: string;
  speedRatio?: number;
};

type TemplateListEventParams = {
  templates: TemplateItem[];
};

type TemplateListEvent = CustomEvent<TemplateListEventParams>;

class AgentUserTemplateListEvents {
  constructor(private eventName: string) { }

  listen(listener: (e: TemplateListEvent) => void): () => void {
    window.addEventListener(this.eventName, listener as EventListener);
    return () => {
      window.removeEventListener(this.eventName, listener as EventListener);
    };
  }

  dispatch(detail: TemplateListEventParams): void {
    const event = new CustomEvent<TemplateListEventParams>(this.eventName, { detail });
    window.dispatchEvent(event);
  }
}

interface AgentUserTemplateListCacheData {
  templates: TemplateItem[];
  page: number;
  pageSize: number;
  total?: number;
}

class AgentUserTemplateListLocal {
  constructor(private storageKey: string) { }

  async get(): Promise<AgentUserTemplateListCacheData | null> {
    return await jsonStore.get<AgentUserTemplateListCacheData>(this.storageKey);
  }

  async set(data: AgentUserTemplateListCacheData): Promise<void> {
    await jsonStore.set(this.storageKey, data);
  }

  async clear(): Promise<void> {
    await jsonStore.set(this.storageKey, null);
  }
}

class AgentUserTemplateListRemote {
  constructor(
    private cache: AgentUserTemplateListLocal,
    private agentId: string,
    private type: "video" | "podcast" | "moments" | "post" | "article" | "image",
    private userId?: string
  ) { }

  async getCached() {
    return await this.cache.get();
  }

  async fetchTemplates(page: number, pageSize: number) {
    const data = await pushInfoDataTemplateList({
      userId: this.userId || "",
      agentId: this.agentId,
      type: this.type,
      page,
      pageSize,
    });
    const templates: TemplateItem[] = data.map((item) => ({
      id: item.id as number,
      uuid: item.uuid,
      name: item.templateName,
      coverUrl: item.coverUrl,
      coverVideo: item.coverVideo,
      templateResource: item.templateResource,
      scene: item.scene as VIDEO_TEMPLATE_TYPE,
      voiceModelId: item.voiceModelId,
      speedRatio: item.speedRatio,
    }));
    await this.cache.set({
      templates,
      page,
      pageSize,
    });
    return {
      templates,
      page,
      pageSize,
    };
  }
  async fetchKnowledgeItems(folderId: number) {
    const data = await knowledgeGetFolderItems(folderId);
    return data.items || [];
  }
  // 获取模板素材设置
  async fetchTemplateMaterialSettings(params: { agentId: string, type: number }) {
    const data = await agentMaterialSettings(params);
    return data;
  }
  // 更新模板素材设置
  async updateMaterialSettings(params: AgentMaterialSettingsUpdateRequest) {
    await agentMaterialSettingsUpdate(params);
  }
  // 删除模板
  async deleteTemplate(templateId: string) {
    await agentDeleteTemplate(templateId);
  }
}

export class AgentUserTemplateListManager {
  private readonly STORAGE_PREFIX = "MIUTA_AGENT_USER_TEMPLATE_LIST";
  private templates: TemplateItem[] = [];
  private page: number = 1;
  private pageSize: number = 1000;

  private cache: AgentUserTemplateListLocal;
  private api: AgentUserTemplateListRemote;
  private events: AgentUserTemplateListEvents;

  constructor(
    private agentId: string,
    private type: "video" | "podcast" | "moments" | "post" | "article" | "image",
  ) {
    const key = `${this.STORAGE_PREFIX}:${this.agentId}:${this.type}`;
    this.cache = new AgentUserTemplateListLocal(key);
    this.api = new AgentUserTemplateListRemote(this.cache, agentId, type);
    this.events = new AgentUserTemplateListEvents(key);
  }

  public getTemplates(): TemplateItem[] {
    return this.templates;
  }

  private setTemplates(templates: TemplateItem[]) {
    this.templates = templates;
    this.dispatch();
  }

  public async init() {
    // try {
    //   const cached = await this.api.getCached();
    //   if (cached) {
    //     this.setTemplates(cached.templates);
    //     this.page = cached.page;
    //     this.pageSize = cached.pageSize;
    //     this.fetch();
    //     return;
    //   }
    // } catch {
    //   this.setTemplates([]);
    //   this.page = 1;
    //   this.pageSize = 20;
    // }
    await this.fetch();
  }

  public async fetch(page?: number, pageSize?: number) {
    const data = await this.api.fetchTemplates(page ?? this.page, pageSize ?? this.pageSize);
    this.setTemplates(data.templates);
    this.page = data.page;
    this.pageSize = data.pageSize;
  }

  public async refresh(page?: number, pageSize?: number) {
    const data = await this.api.fetchTemplates(page ?? this.page, pageSize ?? this.pageSize);
    this.setTemplates(data.templates);
    this.page = data.page;
    this.pageSize = data.pageSize;
  }

  // 获取agent模板素材配置
  public async fetchTemplateMaterialSettings(params: { agentId: string, type: number }) {
    const data = await this.api.fetchTemplateMaterialSettings(params);
    return data;
  }

  // 更新agent模板素材配置
  public async updateMaterialSettings(params: AgentMaterialSettingsUpdateRequest) {
    await this.api.updateMaterialSettings(params);
  }

  // 删除模板
  public async deleteTemplate(templateId: string) {
    await this.api.deleteTemplate(templateId);
  }

  public reset(): void {
    this.setTemplates([]);
    this.page = 1;
    this.pageSize = 1000;
    this.clear();
  }

  public clear(): void {
    this.cache.clear();
  }

  public listen(listener: (e: TemplateListEvent) => void): () => void {
    return this.events.listen(listener);
  }

  private dispatch(): void {
    this.events.dispatch({ templates: this.templates });
  }

  // public listenKnowledgeItemsChange(listener: (e: KnowledgeItemsChangeEvent) => void): () => void {
  //   return this.events.listenKnowledgeItemsChange(listener);
  // }
}
// 导出素材设置更新事件实例
export const materialSettingsUpdateEvent = new MaterialSettingsUpdateEvent();

export const agentUserTemplateListHooks = {
  useTemplateListData(
    props: {
      agentId: string;
      type: "video" | "podcast" | "moments" | "post" | "article" | "image";
      userId?: string;
      page?: number;
      pageSize?: number;
    }
  ) {
    const { agentId, type, userId, page = 1, pageSize = 1000 } = props;
    const managerRef = useRef<AgentUserTemplateListManager | null>(null);
    const [templates, setTemplates] = useState<TemplateItem[]>([]);
    const [loading, setLoading] = useState(false);
    const { showToastByCode } = useToast();

    useEffect(() => {
      managerRef.current = new AgentUserTemplateListManager(agentId, type);

      const init = async () => {
        if (!managerRef.current) return;
        setLoading(true);
        try {
          await managerRef.current.init();
        } catch (error) {
          showToastByCode(error);
        } finally {
          setLoading(false);
        }
      };

      init();

      setTemplates(managerRef.current.getTemplates());

      const unsubscribe = managerRef.current.listen((e) => setTemplates(e.detail.templates));

      return unsubscribe;
    }, [agentId, type, userId]);

    // useEffect(() => {
    //   const init = async () => {
    //     if (!managerRef.current) return;
    //     setLoading(true);
    //     try {
    //       await managerRef.current.init();
    //     } catch (error) {
    //       showToastByCode(error);
    //     } finally {
    //       setLoading(false);
    //     }
    //   };
    //   init();
    // }, [agentId, type, userId, page, pageSize]);

    const refresh = async (nextPage?: number, nextPageSize?: number) => {
      if (!managerRef.current) return;
      setLoading(true);
      try {
        await managerRef.current.refresh(nextPage ?? page, nextPageSize ?? pageSize);
      } catch (error) {
        showToastByCode(error);
      } finally {
        setLoading(false);
      }
    };

    return {
      templates,
      loading,
      refresh,
    };
  },
  // 短视频模板素材配置数据
  useTemplateMaterialSettings({
    scene,
    enabledMaterial,
    enabledNote,
  }: { scene: VIDEO_TEMPLATE_TYPE, enabledMaterial?: boolean, enabledNote?: boolean }) {
    const params = useParams();
    const { showToastByCode, toast } = useToast();
    const { t } = useTranslation();
    const managerRef = useRef<AgentUserTemplateListManager | null>(null);

    // 模板素材配置总数居(包括口播体、混剪体)
    const [agentMaterialSettings, setAgentMaterialSettings] = useState<AgentMaterialSettingsResponseData | null>(null);

    // 模板素材配置
    const [materialSettings, setMaterialSettings] = useState<TemplateMaterialSettingsData | null>(null);
    // 模板素材配置加载中
    const [loading, setLoading] = useState(false);
    // 模板素材配置更新中
    const [updating, setUpdating] = useState(false);

    // 使用素材
    const [openUse, setOpenUse] = useState(false);
    // 公共素材
    const [openPublic, setOpenPublic] = useState(false);
    // 个人素材
    const [openPrivate, setOpenPrivate] = useState(false);
    // 笔记素材
    const [openNote, setOpenNote] = useState(false);
    // 个人素材文件夹和标签配置
    const [personalConfigs, setPersonalConfigs] = useState<TemplateMaterialSettingsData['personalConfigs']>([]);

    // 是否关联素材相册
    const isRelatedMaterial = personalConfigs?.length > 0;

    // 素材是否有标签（有无上传素材）
    const hasTags = personalConfigs?.some((config) => config.tags?.length > 0);

    // 获取agent模板素材配置
    const fetchTemplateMaterialSettings = async () => {
      if (!managerRef.current) return;
      setLoading(true);
      try {
        const data = await managerRef.current.fetchTemplateMaterialSettings({
          agentId: params.id,
          type: 1, // 默认短视频
        });
        setAgentMaterialSettings(data);
        // 在设置状态后立即处理数据
        handleChangeMaterialConfigData(scene, data);
      } catch (error) {
        showToastByCode(error);
      } finally {
        setLoading(false);
      }
    };

    // 根据模板场景类型，设置模板素材配置数据
    const handleChangeMaterialConfigData = (scene: VIDEO_TEMPLATE_TYPE, data?: AgentMaterialSettingsResponseData) => {
      // 获取当前生效的 Key (例如 'broadcast' 或 'broadcastNote')
      const effectiveKey = getEffectiveSceneKey(scene, enabledNote) as keyof AgentMaterialSettingsResponseData;

      // 优先级：传入的 data > 持久化的 agentMaterialSettings
      const sceneData = data?.[effectiveKey] || agentMaterialSettings?.[effectiveKey];
      console.log({ scene, sceneType: effectiveKey })

      if (!sceneData) {
        setOpenUse(false);
        console.log('未获取到模板素材配置数据', { scene, agentMaterialSettings, data })
        return;
      };

      // 初始化个人素材文件夹标签配置
      setPersonalConfigs(sceneData.personalConfigs || []);
      // 初始化开关状态
      setOpenPublic(sceneData.publicEnabled);
      setOpenPrivate(sceneData.personalEnabled);
      setOpenNote(sceneData.noteEnabled);
      // 初始化使用素材开关状态
      setOpenUse(sceneData.publicEnabled || sceneData.personalEnabled || sceneData.noteEnabled);
      setMaterialSettings(sceneData);
    }

    // 更新agent模板素材配置
    const updateMaterialSettings = async () => {
      if (!managerRef.current) return;
      setUpdating(true);
      try {
        const requestParams = {
          agentId: params.id,
          type: 1, // 默认短视频
          // 使用转换后的参数名：传给后端 broadcast 或 broadcast__note
          scene: getUpdateSceneParam(scene, enabledNote),
          publicEnabled: openPublic,
          personalEnabled: openPrivate,
          noteEnabled: openNote,
          selectedFolders: filterPersonalConfigs?.map((folder) => ({
            folderId: folder.folderId,
            tagIds: folder.tags.filter((tag) => tag.isSelected).map((tag) => tag.tagId),
          })),
        } as AgentMaterialSettingsUpdateRequest;
        await managerRef.current.updateMaterialSettings(requestParams);
        toast.default({
          description: t('template_material_range.modal.edit_success'),
        })
        fetchTemplateMaterialSettings();
      } catch (error) {
        showToastByCode(error);
      } finally {
        setUpdating(false);
      }
    };

    useEffect(() => {
      const init = async () => {
        managerRef.current = new AgentUserTemplateListManager(params.id, 'video');
        // 如果启用素材，则获取配置
        if (enabledMaterial) {
          await fetchTemplateMaterialSettings();
        };
      }
      init()
    }, [enabledMaterial, params.id]);

    // 监听素材设置更新事件
    useEffect(() => {
      const unsubscribe = materialSettingsUpdateEvent.listen(() => {
        fetchTemplateMaterialSettings();
      });
      return unsubscribe;
    }, []);

    // 监听场景变化，更新素材配置（切换模板时会触发）
    useEffect(() => {
      if (agentMaterialSettings) {
        handleChangeMaterialConfigData(scene);
      }
    }, [scene, enabledNote])

    useEffect(() => {
      if (!openUse) {
        setOpenPublic(openUse);
        setOpenPrivate(openUse);
        setOpenNote(openUse);
      }
    }, [openUse]);

    useEffect(() => {
      if (enabledNote) {
        if (!openPublic && !openNote) {
          setOpenUse(false);
        }
      } else {
        if (!openPublic && !(openPrivate && isRelatedMaterial)) {
          setOpenUse(false);
        }
      }
    }, [openPublic, openPrivate, openNote, isRelatedMaterial]);

    // 切换使用素材开关(联动其他开关)
    const handleChangeOpenUse = (value: boolean) => {
      setOpenUse(value);
      setOpenPublic(value);
      setOpenNote(value);
      if (isRelatedMaterial) {
        setOpenPrivate(value);
      }
    }

    // 切换选择标签
    const toggleSelectTag = (tagId: number) => {
      setPersonalConfigs((prevConfigs) =>
        prevConfigs.map((folder) => {
          // 检查当前 folder 是否包含目标 tag
          const hasTag = folder.tags.some((tag) => tag.tagId === Number(tagId));

          if (!hasTag) return folder;

          // 更新 tags 列表并重新计算该 folder 的选中总数
          let newSelectTotal = 0;
          const newTags = folder.tags.map((tag) => {
            const isTarget = tag.tagId === Number(tagId);
            const nextSelected = isTarget ? !tag.isSelected : tag.isSelected;

            if (nextSelected) newSelectTotal++; // 累加计数

            return isTarget ? { ...tag, isSelected: nextSelected } : tag;
          });

          return {
            ...folder,
            tags: newTags,
            selectTotal: newSelectTotal, // 同步更新总数
          };
        })
      );
    };

    // 全选/取消全选指定文件夹下的标签
    const toggleSelectTagAll = (folderId: number, checked: boolean) => {
      setPersonalConfigs((prevConfigs) =>
        prevConfigs.map((folder) => {
          // 如果不是目标文件夹，直接返回原数据
          if (folder.folderId !== folderId) return folder;

          // 如果是目标文件夹，更新该文件夹下所有标签的选中状态
          return {
            ...folder,
            tags: folder.tags.map((tag) => ({
              ...tag,
              isSelected: checked,
            })),
            // 同步更新该文件夹的选中总数
            selectTotal: checked ? folder.tags.length : 0,
          };
        })
      );
    };

    // 过滤出标签选中的数据
    const filterPersonalConfigs = useMemo(() => {
      if (!personalConfigs) return [];
      return personalConfigs.reduce((acc, folder) => {
        // 筛选出当前 folder 下被选中的 tags
        const selectedTags = folder.tags.filter(tag => tag.isSelected);
        // 如果有选中的 tag，则克隆 folder 并更新其 tags
        if (selectedTags.length > 0) {
          acc.push({
            ...folder,
            tags: selectedTags,
          });
        }
        return acc;
      }, []) as typeof personalConfigs;
    }, [personalConfigs]);

    // 计算选中的标签总数
    const selectedTagCount = useMemo(() => {
      // 如果数据不存在，直接返回 0
      if (!personalConfigs) return 0;

      return personalConfigs.reduce((total, folder) => {
        // 累加当前 folder 下 isSelected 为 true 的 tag 数量
        const folderSelectedCount = folder.tags.filter(tag => tag.isSelected).length;
        return total + folderSelectedCount;
      }, 0);
    }, [personalConfigs]);

    return {
      // 打开状态
      openUse,
      openPublic,
      openPrivate,
      openNote,
      setOpenUse: handleChangeOpenUse,
      setOpenPublic,
      setOpenPrivate,
      setOpenNote,
      // 个人素材文件夹和标签配置
      /** 是否关联素材相册 */
      isRelatedMaterial,
      personalConfigs,
      toggleSelectTag,
      toggleSelectTagAll,
      filterPersonalConfigs,
      selectedTagCount,
      hasTags,
      // 素材配置
      materialSettings,
      loading,
      fetchTemplateMaterialSettings,
      updateMaterialSettings,
      updating,

      handleChangeMaterialConfigData,
    }
  },
  // 公共数字人和音频列表
  usePublicTemplatesData() {
    const { toast, showToastByCode } = useToast();
    const [publicActors, setPublicActors] = useState<PublicActorTemplateData[]>([]);
    const [publicVoices, setPublicVoices] = useState<PublicAudioTemplateData[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    // 获取公共数字人列表
    const fetchPublicActors = async ({
      sid,
      gender,
      name,
    }: {
      sid?: number | null;
      gender?: string;
      name?: string;
    }) => {
      const isLoadMore = sid != null;
      setLoading(true);
      try {
        const data = await agentUserActorsFilter({
          pageSize: 50,
          sid: sid ?? undefined,
          gender,
          name,
        });
        const newActors = data.actors ?? [];
        // 分页加载时累加；loadMore 可能返回空页，不能覆盖已加载数据
        setPublicActors(prev =>
          isLoadMore
            ? newActors.length === 0
              ? prev
              : [
                ...prev,
                ...newActors.filter(item => !prev.some(p => p.id === item.id)),
              ]
            : newActors
        );
        return data;
      } catch (error) {
        console.error(error)
        showToastByCode(error);
      } finally {
        setLoading(false);
      }
    };

    // 获取公共音频列表
    const fetchPublicAudios = async ({
      sid,
      provider,
      name,
    }: {
      sid?: number | null;
      provider?: string;
      name?: string;
    }) => {
      const isLoadMore = sid != null;
      setLoading(true);
      try {
        const data = await agentUserAudioTemplatesFilter({
          pageSize: 50,
          sid: sid ?? undefined,
          provider,
          name,
        });
        const newTemplates = data.templates ?? [];
        setPublicVoices(prev =>
          isLoadMore
            ? newTemplates.length === 0
              ? prev
              : [
                  ...prev,
                  ...newTemplates.filter(
                    item => !prev.some(p => p.voiceTtsId === item.voiceTtsId)
                  ),
                ]
            : newTemplates
        );
        return data;
      } catch (error) {
        console.error(error)
        showToastByCode(error);
      } finally {
        setLoading(false);
      }
    };


    return {
      loading,
      publicActors,
      publicVoices,
      fetchPublicActors,
      fetchPublicAudios,
    }
  },
  // 删除模板
  useDeleteTemplate() {
    const [loading, setLoading] = useState(false);
    const { showToastByCode } = useToast();

    const deleteTemplate = async (templateId: string) => {
      setLoading(true);
      try {
        await agentDeleteTemplate(templateId);
      } catch (error) {
        showToastByCode(error);
      } finally {
        setLoading(false);
      }
    };

    return {
      loading,
      deleteTemplate,
    }
  }
};

export default AgentUserTemplateListManager;
